import { useState } from "react";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/site";

const navMain = [
  { label: "首页", to: "/" },
  { label: "本期瓜单", to: "/#gua" },
  { label: "瓜点雷达", to: "/#radar" },
  { label: "吃瓜指南", to: "/#zhinan" },
];

function BrandMark({ compact }: { compact?: boolean }) {
  return (
    <Link to="/" className="flex items-center gap-2.5 group cursor-pointer whitespace-nowrap">
      <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50 font-hl text-xl leading-none shadow-soft">
        瓜
        <span className="absolute -right-1 -top-1 flex h-3 w-3 items-center justify-center rounded-full bg-accent-500">
          <span className="block h-1 w-1 rounded-full bg-background-50" />
        </span>
      </span>
      <span className="flex flex-col leading-none">
        <span className="font-display text-lg font-black tracking-tight text-foreground-950">
          吃瓜日报
        </span>
        {!compact && (
          <span className="text-[10px] tracking-[0.28em] text-primary-600 uppercase mt-0.5">
            Melon Daily
          </span>
        )}
      </span>
    </Link>
  );
}

export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setCatOpen(false);
  }, [location.pathname, location.hash]);

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/" && !location.hash;
    const hash = to.split("#")[1];
    return location.pathname === "/" && !!location.hash && location.hash === `#${hash}`;
  };

  return (
    <header
      className={`sticky top-0 z-50 border-b transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md border-background-200 shadow-soft"
          : "bg-background-50 border-background-200"
      }`}
    >
      {/* ticker */}
      <div className="hidden lg:block border-b border-background-200 bg-background-100">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 md:px-6 py-1.5 text-xs">
          <span className="inline-flex items-center gap-1.5 font-medium text-primary-700 whitespace-nowrap">
            <i className="ri-flashlight-line" />
            瓜点快讯
          </span>
          <div className="flex items-center gap-4 overflow-hidden text-foreground-600">
            {["恋综话术对线刷屏", "音乐节拼盘退票风波", "悬疑剧结局引争议"].map((t, i) => (
              <span key={t} className="flex items-center gap-1.5 whitespace-nowrap">
                <i className={`ri-circle-fill text-[5px] ${i % 2 ? "text-accent-500" : "text-primary-500"}`} />
                {t}
              </span>
            ))}
          </div>
          <span className="ml-auto text-foreground-500 whitespace-nowrap">更新于 2026.09.08</span>
        </div>
      </div>

      {/* main nav */}
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex h-16 items-center justify-between gap-4">
          <BrandMark />

          <nav className="hidden lg:flex items-center gap-1" aria-label="主导航">
            {navMain.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className={`rounded-md px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                  isActive(item.to)
                    ? "text-primary-700 bg-primary-100"
                    : "text-foreground-700 hover:text-primary-700 hover:bg-primary-50"
                }`}
              >
                {item.label}
              </Link>
            ))}
            <div className="relative">
              <button
                type="button"
                onClick={() => setCatOpen((v) => !v)}
                className={`flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium transition-colors cursor-pointer ${
                  catOpen ? "text-primary-700 bg-primary-100" : "text-foreground-700 hover:text-primary-700 hover:bg-primary-50"
                }`}
                aria-haspopup="menu"
                aria-expanded={catOpen}
              >
                吃瓜专区
                <i className={`ri-arrow-down-s-line text-base transition-transform ${catOpen ? "rotate-180" : ""}`} />
              </button>
              {catOpen && (
                <div className="absolute right-0 top-full mt-2 w-52 rounded-xl border border-background-200 bg-background-50 p-1.5 shadow-lift">
                  {categories.map((c) => (
                    <Link
                      key={c.id}
                      to={`/#gua?cat=${c.id}`}
                      onClick={() => setCatOpen(false)}
                      className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 cursor-pointer"
                    >
                      <i className={`${c.icon} text-base text-accent-600`} />
                      {c.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/about#contact"
              className="hidden sm:inline-flex whitespace-nowrap items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-send-plane-line text-base" />
              投稿线索
            </Link>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-label="打开菜单"
              aria-expanded={open}
              className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-md border border-background-200 text-foreground-800 hover:bg-background-100 cursor-pointer"
            >
              <i className={open ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
            </button>
          </div>
        </div>
      </div>

      {/* mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-background-200 bg-background-50">
          <nav className="mx-auto max-w-7xl px-4 md:px-6 py-3 space-y-1" aria-label="移动端导航">
            {navMain.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="flex items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium text-foreground-800 hover:bg-background-100 cursor-pointer"
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-foreground-400" />
              </Link>
            ))}
            <p className="px-3 pt-2 pb-1 text-xs font-semibold text-foreground-500">吃瓜专区</p>
            {categories.map((c) => (
              <Link
                key={c.id}
                to={`/#gua?cat=${c.id}`}
                className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer"
              >
                <i className={`${c.icon} text-accent-600`} />
                {c.name}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}